import { AxiosResponse } from 'axios'
import { FC, useCallback, useState } from 'react'
import { Controller } from 'react-hook-form'

import { Autocomplete, TextField, CircularProgress, debounce } from '@mui/material'

import { axiosApi } from 'api/index'

interface AutoCompleteOption {
  value: string
  label: string
}

interface AutoCompleteProps {
  sx?: any
  control: any
  name: string
  disabled?: boolean
  size?: 'small' | 'medium'
  placeholder?: string
  label: string
  error?: boolean
  helperText?: string
  column: string
  url: string
}

const AutoComplete: FC<AutoCompleteProps> = ({
  sx,
  control,
  name,
  disabled = false,
  size = 'small',
  placeholder,
  label,
  error = false,
  helperText,
  column,
  url
}) => {
  const [options, setOptions] = useState<AutoCompleteOption[]>([])
  const [loading, setLoading] = useState(false)

  const fetchOptions = async (searchString: string) => {
    if (!searchString || searchString.length < 2) {
      setOptions([])
      return
    }

    setLoading(true)
    try {
      const { data: responseData }: AxiosResponse<{ suggestions: any[]; data?: any[] }> =
        await axiosApi.post(url, {
          column: 'name',
          search_string: searchString,
          search_map: {
            name: 'name',
            value: 'name',
          },
        })

      let newOptions: { value: string; label: string }[] = []

      if (responseData && responseData.data && Array.isArray(responseData.data)) {
        newOptions = responseData.data.map((item: any) => ({
          value: item.value || item.name || '',
          label: item.name || item.value || '',
        }))
      } else if (Array.isArray(responseData)) {
        newOptions = responseData.map((item: any) => ({
          value: item.value || item.name || '',
          label: item.name || item.value || '',
        }))
      }

      setOptions(newOptions)
    } catch (error) {
      console.error('Error fetching autocomplete options:', error)
      setOptions([])
    } finally {
      setLoading(false)
    }
  }

  const debouncedFetchOptions = useCallback(debounce(fetchOptions, 300), [column])
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange: fieldOnChange, value, ref } }) => (
        <Autocomplete
          sx={sx}
          options={options}
          loading={loading}
          disabled={disabled}
          size={size}
          placeholder={placeholder}
          value={value ? { value, label: value } : null}
          onInputChange={(event, newInputValue) => {
            debouncedFetchOptions(newInputValue)
          }}
          onChange={(event, newValue) => {
            if (newValue) {
              const value = typeof newValue === 'string' ? newValue : newValue.value
              fieldOnChange(value)
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              error={error}
              helperText={helperText}
              placeholder={placeholder}
              inputRef={ref}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              onKeyDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
            />
          )}
          renderOption={(props, option) => <li {...props}>{option.label || option.value}</li>}
          getOptionLabel={(option) => option.label || option.value || ''}
          isOptionEqualToValue={(option, value) => option.value === value.value}
        />
      )}
    />
  )
}

export default AutoComplete
